import { Gallery } from 'iconsax-react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import AppCell from '@/components/common/app-cell';
import { AvatarWithStatus } from '@/components/common/avatar-with-status';
import CopyButton from '@/components/common/copy-button';
import Linkable from '@/components/common/linkable';
import InputModal from '@/components/common/modal/imput-model';
import type { ModalAction } from '@/components/common/modal/modal';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import LoadingLayout from '@/components/layouts/progress-layout/loading-layout';
import QrCodeModal from '@/components/layouts/qr-code-modal';
import { UserAvatarUploadButton } from '@/components/mine/user-avatar-upload-button';
import { useBrand } from '@/hooks/pwa/use-brand';
import useUserPlatform from '@/hooks/use-user-platform';
import useCancelAccount from '@/hooks/users/use-cancel-account';
import useUpdateNickname from '@/hooks/users/use-update-nickname';
import useUpdateUserSign from '@/hooks/users/use-update-user-sign';
import useUserInfo from '@/hooks/users/use-user-info';
import ROUTES from '@/routes';
import { makePrompt } from '@/stores/prompter';
import { addToast } from '@/stores/toast';

export default function MyInfoRoute() {
  const { t } = useTranslation();
  const brand = useBrand();
  const navigate = useNavigate();

  const nicknameModal = useRef<ModalAction>(null);
  const signatureModal = useRef<ModalAction>(null);
  const qrCodeModal = useRef<ModalAction>(null);

  const { isEmbedded, isDesktop } = useUserPlatform();
  const { data: userInfo, isLoading } = useUserInfo();
  const { mutateAsync: cancelAccount } = useCancelAccount();
  const { mutateAsync: updateNickname } = useUpdateNickname();
  const { mutateAsync: updateSignature } = useUpdateUserSign();

  const handleDeleteAccount = async () => {
    if (
      await makePrompt({
        key: 'delete-account',
        type: 'dialog',
        title: t('personal-info.delete-account.title'),
        content: t('personal-info.delete-account.alert'),
      })
    ) {
      const res = await cancelAccount();

      if (res?.Code === 0) {
        navigate(ROUTES.AUTH.SIGN_IN.$path());
      }
    }
  };

  return (
    <PageLayout
      title={t('mine.personal-info')}
      headerConfig={{
        navigateBack: !isDesktop,
      }}>
      <LoadingLayout promise={isLoading}>
        {() => (
          <div className='mx-1 flex flex-col desktop:mx-4'>
            {isDesktop ? (
              <div className='relative w-fit px-4 mb-2'>
                <AvatarWithStatus
                  className='size-16'
                  size={[64, 64]}
                  imageUrl={userInfo?.HeadImg}
                />
                <UserAvatarUploadButton>
                  <div className='btn btn-circle btn-xs absolute bottom-0 right-0 bg-neutral-0 shadow-button'>
                    <Gallery className='size-3 text-metal-700' />
                  </div>
                </UserAvatarUploadButton>
              </div>
            ) : (
              <UserAvatarUploadButton>
                <AppCell
                  label={
                    <AvatarWithStatus
                      className='size-12'
                      size={[48, 48]}
                      imageUrl={userInfo?.HeadImg}
                    />
                  }
                  chevron={!isEmbedded}
                />
              </UserAvatarUploadButton>
            )}

            <Linkable
              linkable={!(isEmbedded || isDesktop)}
              to={ROUTES.MY.NICKNAME.$path()}>
              <AppCell
                hoverable={isDesktop}
                label={t('personal-info.nickname')}
                content={userInfo?.NickName ?? ''}
                chevron={!(isEmbedded || isDesktop)}
                onClick={() => isDesktop && nicknameModal.current?.open()}
              />
            </Linkable>
            <InputModal
              ref={nicknameModal}
              title={t('personal-info.nickname')}
              defaultValue={userInfo?.NickName}
              onSubmit={({ modalInputValue }) => {
                updateNickname(modalInputValue);
              }}
            />
            <Linkable
              linkable={!(isEmbedded || isDesktop)}
              to={ROUTES.MY.SIGNATURE.$path()}>
              <AppCell
                hoverable={isDesktop}
                label={t('personal-info.signature')}
                content={<span className='truncate'>{userInfo?.Sign ?? ''}</span>}
                chevron={!isDesktop}
                onClick={() => isDesktop && signatureModal.current?.open()}
              />
            </Linkable>
            <InputModal
              ref={signatureModal}
              title={t('personal-info.signature')}
              defaultValue={userInfo?.Sign}
              onSubmit={({ modalInputValue }) => {
                updateSignature(modalInputValue);
              }}
            />
            <hr className='m-2' />

            {!isEmbedded && (
              <AppCell
                hoverable={isDesktop}
                content={userInfo ? `+${userInfo.TelCode} ${userInfo.Phone}` : ''}
                label={t('personal-info.phone')}
              />
            )}

            <AppCell
              hoverable={isDesktop}
              className='py-1'
              content={
                userInfo?.VChatID && (
                  <>
                    <span className='font-mono flex items-center gap-2 h-12'>{userInfo.VChatID}</span>
                    <CopyButton content={userInfo.VChatID} />
                  </>
                )
              }
              label={t('user.chat-id', { brandShortName: brand.shortName })}
              onClick={() => {
                if (userInfo?.VChatID) {
                  navigator.clipboard.writeText(userInfo.VChatID);
                  addToast({ type: 'success', message: t('common.copy-success') });
                }
              }}
            />
            <Linkable
              linkable={!isDesktop}
              to={ROUTES.MY.QR_CODE.$path()}>
              <AppCell
                hoverable={isDesktop}
                label={t('personal-info.qr-code')}
                chevron={!isDesktop}
                onClick={() => {
                  if (isDesktop) {
                    qrCodeModal.current?.open();
                  }
                }}
              />
            </Linkable>
            <QrCodeModal ref={qrCodeModal} />
            <hr className='m-2 mode-embedded:hidden' />
            <AppCell
              hoverable={isDesktop}
              label={t('personal-info.delete-account.title')}
              labelClassName='text-error hover:bg-error/10 mode-embedded:hidden'
              onClick={handleDeleteAccount}
            />
          </div>
        )}
      </LoadingLayout>
    </PageLayout>
  );
}
